import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n } from "@/lib/i18n";
import NewsletterForm from "./NewsletterForm";
import { api } from "@/lib/api";

export default function Footer() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <footer className="bg-[#0F1F1F] text-[#E5EBEA] mt-24" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Logo variant="light" />
            <p className="mt-5 text-sm leading-relaxed text-[#B7C3C1] max-w-xs font-body">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 space-y-2 text-sm text-[#B7C3C1]">
              {settings && (
                <>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{settings.legal_address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0" />
                    <a href={`mailto:${settings.email}`} className="hover:text-white transition-colors">
                      {settings.email}
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-editorial mb-4 text-[#8FA09E]">{t("footer.company")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/chi-siamo" className="hover:text-white transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/scienza-qualita" className="hover:text-white transition-colors">{t("nav.science")}</Link></li>
              <li><Link to="/journal" className="hover:text-white transition-colors">{t("nav.journal")}</Link></li>
              <li><Link to="/professionisti" className="hover:text-white transition-colors">{t("nav.pro")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-editorial mb-4 text-[#8FA09E]">{t("footer.products")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/prodotti?linea=pet" className="hover:text-white transition-colors"><span className="text-pet">•</span> {t("nav.pet")}</Link></li>
              <li><Link to="/linea-human" className="hover:text-white transition-colors"><span className="text-human">•</span> {t("nav.human")}</Link></li>
              <li><Link to="/prodotti/pet/articolax" className="hover:text-white transition-colors">ARTICOLAX</Link></li>
              <li><Link to="/prodotti/pet/florax" className="hover:text-white transition-colors">FLORAX</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-editorial mb-4 text-[#8FA09E]">Newsletter</h4>
            <NewsletterForm compact />
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-[#1E3131] flex flex-col md:flex-row justify-between gap-6 text-xs text-[#8FA09E]">
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">{t("footer.privacy")}</Link>
            <Link to="/cookie" className="hover:text-white transition-colors" data-testid="footer-cookie">{t("footer.cookie")}</Link>
            <Link to="/termini" className="hover:text-white transition-colors">{t("footer.terms")}</Link>
            <button
              type="button"
              onClick={() => window.__bnx_open_cookie_prefs && window.__bnx_open_cookie_prefs()}
              className="hover:text-white transition-colors"
              data-testid="footer-cookie-prefs"
            >
              {t("footer.cookie_prefs")}
            </button>
            <Link to="/accessibilita" className="hover:text-white transition-colors">{t("footer.accessibility")}</Link>
            <Link to="/admin/login" className="hover:text-white transition-colors">{t("footer.admin")}</Link>
          </div>
          <div className="flex items-center gap-4">
            {settings?.instagram && (
              <a
                href={settings.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
            )}
            {settings?.facebook && (
              <a
                href={settings.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-white transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>

        <div className="mt-6 text-[11px] text-[#6E7E7C] leading-relaxed">
          {settings?.company_legal_name || "BioNutrix Srl"} — P.IVA {settings?.vat_number || "IT03110550849"} — C.F. {settings?.fiscal_code || "03110550849"}<br />
          © {year} {settings?.company_legal_name || "BioNutrix Srl"}. {t("footer.copyright")}
        </div>
      </div>
    </footer>
  );
}
